import * as Accordion from "@radix-ui/react-accordion";
import React from "react";
import SVG from "react-inlinesvg";
import { jt, t } from "ttag";

import iconCaretDown from "../../../../img/icons/caret-down.svg";
import iconCaretUp from "../../../../img/icons/caret-up.svg";
import Link from "../../../common/Link";
import { LineStatus } from "../../../constants";
import { ChatLink } from "../../chat/ChatLink";
import { TrackShipmentDetailsCustomerInfo } from "../TrackShipmentDetailsCustomerInfo";
import { TrackShipmentDetailsLines } from "../TrackShipmentDetailsLines";
import { TrackShipmentDetailsSidebar } from "../TrackShipmentDetailsSidebar";
import { TrackShipmentDetailsTimeline } from "../TrackShipmentDetailsTimeline";
import { useSiteURLs } from "../hooks";
import {
    OrderPipelineConfig,
    UnifiedOrderLookupOrder,
    UnifiedOrderLookupOrderShipment,
} from "../types";
import {
    buildOrderTitle,
    formatScheduledDate,
    getClosestStep,
    getUnifiedEvents,
    shouldShowDeliveryDate,
    shouldShowTrackingNumber,
} from "../utils";

import lineStyles from "../TrackShipmentDetailsLines/index.module.scss";
import styles from "./index.module.scss";

const AccordionIcon = () => (
    <>
        <SVG aria-hidden="true" data-name="closed" src={iconCaretDown} />
        <SVG aria-hidden="true" data-name="open" src={iconCaretUp} />
    </>
);

export const TrackShipmentDetails = (props: {
    order: UnifiedOrderLookupOrder;
    shipment: UnifiedOrderLookupOrderShipment;
    config: OrderPipelineConfig | null;
    war_link: string;
}): React.JSX.Element => {
    const {
        chatOfflineURL,
        customerServiceURL,
        customerLoginURL,
        customerRegisterURL,
    } = useSiteURLs();

    if (!props.config) {
        return <></>;
    }

    const closestStep = getClosestStep(
        props.shipment.events,
        props.order.source,
    );
    let deliverySteps:
        | typeof props.config.freight_delivery_steps
        | typeof props.config.trs_delivery_steps
        | null = props.config.freight_delivery_steps || null;

    if (props.order.source === "TRS") {
        deliverySteps = props.config.trs_delivery_steps || null;
    }
    const unified_events = getUnifiedEvents(
        props.order,
        props.shipment,
        deliverySteps,
        closestStep,
        false,
    );
    const showDeliveryDate = shouldShowDeliveryDate(closestStep);
    const showTrackingNumber = shouldShowTrackingNumber(closestStep);
    const title = buildOrderTitle(closestStep);
    const scheduledDate = formatScheduledDate(props.shipment.scheduled_date);
    const chatLink = (
        <ChatLink
            className={styles.supportChatButton}
            chatOfflineLink={chatOfflineURL}
        />
    );
    const supportLink = (
        <Link className={styles.supportLink} href={customerServiceURL}>
            {t`Help Center`}
        </Link>
    );

    let sidebarContent = props.config.tracking_page_sidebar?.value;
    if (props.order.source === "TRS") {
        sidebarContent = props.config.tracking_page_sidebar_trs?.value;
    }

    return (
        <>
            <h2>{t`${title}`}</h2>
            {showDeliveryDate && scheduledDate && (
                <p className={styles.deliveryDate}>
                    {closestStep === LineStatus.DELIVERED
                        ? t`On ${scheduledDate}`
                        : t`Delivery Date: ${scheduledDate}`}
                </p>
            )}
            <div
                className={
                    showDeliveryDate
                        ? styles.containerCondensed
                        : styles.container
                }
            >
                <div className={styles.timelineWrapper}>
                    <div className={styles.timelineMetadata}>
                        <TrackShipmentDetailsCustomerInfo
                            shippingAddress={props.order.shipping_address}
                            customerLoginURL={customerLoginURL}
                            customerRegisterURL={customerRegisterURL}
                        />
                    </div>
                    <div className={styles.timelineMetadata}>
                        <div className={styles.metadataLine}>
                            <h3 className={styles.title}>{t`Order Number`}:</h3>
                            <p className={styles.copy}>
                                {props.order.searched_order_number}
                            </p>
                        </div>
                        <div className={styles.metadataLine}>
                            {showTrackingNumber && (
                                <>
                                    <h3 className={styles.title}>
                                        {t`Tracking Number`}:
                                    </h3>
                                    {props.shipment.last_mile_tracking_number}
                                </>
                            )}
                        </div>
                        {showDeliveryDate && scheduledDate && (
                            <div className={styles.metadataLine}>
                                <h3 className={styles.title}>
                                    {t`Delivery Date`}:
                                </h3>
                                <p className={styles.copy}>{scheduledDate}</p>
                            </div>
                        )}
                    </div>
                    <TrackShipmentDetailsTimeline
                        deliveryDate={scheduledDate}
                        trackingNumber={
                            props.shipment.last_mile_tracking_number
                        }
                        unifiedEvents={unified_events}
                    />
                </div>
                {sidebarContent && (
                    <TrackShipmentDetailsSidebar sidebar={sidebarContent} />
                )}
                <div className={styles.shipment}>
                    <Accordion.Root
                        type="single"
                        collapsible
                        defaultValue="shipment"
                        className={styles.accordionWrapper}
                    >
                        <Accordion.Item value="shipment">
                            <Accordion.Trigger
                                className={styles.accordionTrigger}
                            >
                                {t`Shipment Details`} <AccordionIcon />
                            </Accordion.Trigger>
                            <Accordion.Content>
                                <TrackShipmentDetailsLines
                                    styles={lineStyles}
                                    source={props.order.source}
                                    closestStep={closestStep}
                                    lines={props.shipment.lines}
                                    warLink={props.war_link}
                                />
                            </Accordion.Content>
                        </Accordion.Item>
                    </Accordion.Root>
                    <p>
                        {jt`Need assistance? ${chatLink} or visit our ${supportLink}.`}
                    </p>
                </div>
            </div>
        </>
    );
};
